import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { action, computed, reaction } from 'mobx';
import { withStyles } from 'material-ui/styles';
import classnames from 'classnames'
import { TREE_EVENT } from 'shared/dictionaries/events';

import TreeNodeModel from 'models/TreeNode';

import { InformationOutline, PlusBoxOutline, MinusBoxOutline } from 'mdi-material-ui';
import { ListItem, ListItemIcon, ListItemText, ListItemSecondaryAction } from 'material-ui/List';
import { CircularProgress } from 'material-ui/Progress';
import IconButton from 'material-ui/IconButton';
import NodePrimaryText from 'components/list/NodePrimaryText';
import NodeSecondaryText from 'components/list/NodeSecondaryText';

import { timeout } from 'shared/utils';
import withAppStore from 'decorators/withAppStore';

import styles from 'styles/components/TreeNode';

@withStyles(styles)
@withAppStore
@observer
export default class TreeNode extends Component {

  static propTypes = {
    node: PropTypes.instanceOf(TreeNodeModel).isRequired,
  }

  @computed
  get isLoading() {
    return this.props.node.isLoadingChildren;
  }

  @computed
  get isExpanded() {
    return this.props.node.isExpanded;
  }

  @computed
  get isFinal() {
    return this.props.node.isFinal;
  }

  get isInfoAllowed() {
    return this.appStore.treeStore.isInfoSupported && !this.appStore.disableInfo;
  }

  @computed
  get showRightAction() {
    return this.props.node.hasInfo && !this.isFinal && this.isInfoAllowed;
  }

  @computed
  get icon() {
    const collapsedIcon = <PlusBoxOutline />;
    const expandedIcon = <MinusBoxOutline />;
    const loadingIcon = <CircularProgress size={24} />;

    return this.isLoading ? loadingIcon : this.isExpanded ? expandedIcon : collapsedIcon;
  }

  @computed
  get nodeClassNames() {
    const { classes, node } = this.props;

    return classnames(
      [classes.root],
      {
        [classes.hovered]: node.isHovered,
        [classes.flashed]: node.isFlashed
      }
    );
  }

  componentWillMount() {
    reaction(
      () => this.appStore && this.appStore.dense,
      () => this.forceUpdate()
    )
  }

  render() {
    const { classes, node } = this.props;

    return (
      <ListItem
        button
        onClick={this.handleClick}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        className={this.nodeClassNames}
        ContainerProps={{
          'data-node-id': node.id
        }}
      >
        {!this.isFinal &&
          <ListItemIcon className={classes.icon}>{this.icon}</ListItemIcon>
        }
        <ListItemText
          disableTypography
          inset={!this.isFinal}
          primary={<NodePrimaryText node={node} />}
          secondary={<NodeSecondaryText node={node} />}
        />
        <ListItemSecondaryAction>
          {this.showRightAction &&
            <IconButton aria-label="Справка" onClick={this.handleInfoClick}>
              <InformationOutline />
            </IconButton>
          }
        </ListItemSecondaryAction>
      </ListItem>
    )
  }

  @action.bound
  handleClick() {
    this.appStore.clearFlashHighlight();
    if (this.isFinal) {
      this.showInfo();
    } else if (this.isExpanded) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  @action.bound
  handleMouseEnter() {
    this.props.node.setHovered(true);
  }

  @action.bound
  handleMouseLeave() {
    this.props.node.setHovered(false);
  }

  @action.bound
  handleInfoClick() {
    this.showInfo();
  }

  async expand() {
    const { node } = this.props;
    const { id, code } = node;

    if (node.needsToLoadChildren) {
      await node.fetchChildNodes();
      await timeout(10);
    }

    node.isExpanded = true;

    this.appStore.updateExpandedNodes();

    const { expanded } = this.appStore.queryParamsStore.queryParams;
    this.appStore.sendEvent(TREE_EVENT.EXPAND, { id, code, expanded });
  }

  collapse() {
    const { node } = this.props;
    const { id, code } = node

    node.isExpanded = false;

    this.appStore.updateExpandedNodes();

    const { expanded } = this.appStore.queryParamsStore.queryParams;
    this.appStore.sendEvent(TREE_EVENT.COLLAPSE, { id, code, expanded });
  }

  showInfo() {
    const { node } = this.props;
    const { id, code } = node;

    if (this.isInfoAllowed) {
      this.appStore.infoCode = code;
    }
    this.appStore.sendEvent(TREE_EVENT.SHOW_INFO, { id, code })
  }

}



// WEBPACK FOOTER //
// ./src/components/TreeNode.js